import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Facebook,
  MapPin,
  Check,
  ChevronRight,
  Info,
  Navigation,
  DoorOpen,
  X,
} from "lucide-react";

import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import { OFFERS as STATIC_OFFERS } from "@/data/site";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PoliteNotice = ({ compact = false }) => (
  <div
    data-testid="polite-notice"
    className={`rounded-3xl border border-champagne/50 bg-gradient-to-br from-blush to-secondary/40 ${
      compact ? "p-5" : "p-6 md:p-7"
    }`}
  >
    <div className="flex items-start gap-3">
      <span className="rounded-full bg-plum text-white p-2 shrink-0">
        <Info size={14} />
      </span>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-plum font-semibold">
          Polite Notice
        </div>
        <h4 className="font-display text-xl md:text-2xl text-plum-deep mt-1">
          Booking & cancellation policy
        </h4>
        <ul
          className={`mt-3 space-y-2 text-foreground/75 leading-relaxed ${
            compact ? "text-xs" : "text-sm"
          }`}
        >
          <li>
            A <strong className="text-plum">50% deposit</strong> will be taken upon booking
            to secure your appointment slot.
          </li>
          <li>
            <strong className="text-plum">No less than 24 hours notice</strong> must be given
            if you wish to amend, move or cancel your appointment.
          </li>
          <li>
            Late cancellations and <strong className="text-plum">"no shows"</strong> will
            result in <strong className="text-plum">100% treatment charge</strong> and the
            deposit will be lost.
          </li>
          <li>
            Please always give notice if you are unable to make your appointment. Thank you.
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 text-sm">
    <span className="text-foreground/55 uppercase tracking-[0.15em] text-xs">{label}</span>
    <span className="font-medium text-plum text-right">{value}</span>
  </div>
);

export default function Contact() {
  usePageMeta(
    "Book a Treatment · Beautify — Wadebridge, Cornwall",
    "Book your massage, facial, lash or nail treatment with Deborah Louise in Wadebridge, Cornwall. Send an enquiry online and Deborah will confirm your slot personally."
  );
  const settings = useSettings();
  const BRAND = settings.brand;
  const LOCATIONS = settings.locations;
  const TREATMENTS = settings.treatments;
  const FAQS = Array.isArray(settings.faqs) ? settings.faqs : [];
  const OFFERS = settings.offers;
  const EVENT = settings.event;
  const offerItems =
    OFFERS && OFFERS.active && Array.isArray(OFFERS.items) ? OFFERS.items : [];

  // The featured event contributes an extra "special" pool item that clients
  // can pick from the dropdown and that ?special=<event name> can pre-select.
  const eventSpecialItem = (() => {
    if (!EVENT || !EVENT.active) return null;
    if (EVENT.end_date) {
      const end = new Date(`${EVENT.end_date}T23:59:59`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isNaN(end.getTime()) || end.getTime() < today.getTime()) return null;
    }
    const name =
      EVENT.cta_special_name || EVENT.title || "Skincare event";
    const price = EVENT.price_label || "";
    return { name, price };
  })();
  const allSpecialItems = eventSpecialItem
    ? [eventSpecialItem, ...offerItems]
    : offerItems;
  const routerLocation = useLocation();

  // Static "Other" enquiry types reachable via ?enquiry=<key> deep-links.
  const OTHER_ENQUIRIES = {
    "gift-voucher": "Gift voucher enquiry",
    "pamper-party": "Pamper party enquiry",
    "mobile-treatment": "Mobile treatment enquiry",
  };

  // Compute initial treatment from ?special= query param so the Select value
  // is populated on the very first render (avoids a state-commit race with
  // the Radix Select mount + SettingsContext fetch).
  const initialTreatment = (() => {
    if (typeof window === "undefined") return "";
    const search = routerLocation.search || window.location.search;
    if (!search) return "";
    const params = new URLSearchParams(search);
    const enquiryKey = (params.get("enquiry") || "").trim().toLowerCase();
    if (enquiryKey && OTHER_ENQUIRIES[enquiryKey]) return OTHER_ENQUIRIES[enquiryKey];
    const wanted = params.get("special");
    if (!wanted) return "";
    const pool =
      allSpecialItems.length > 0
        ? allSpecialItems
        : Array.isArray(STATIC_OFFERS?.items)
        ? STATIC_OFFERS.items
        : [];
    const match = pool.find(
      (it) => it.name.trim().toLowerCase() === wanted.trim().toLowerCase()
    );
    return match ? `✨ Special: ${match.name} (${match.price})` : "";
  })();

  const [form, setForm] = useState(() => ({
    name: "",
    phone: "",
    email: "",
    preferred_time: "",
    notes: "",
  }));
  const [selectedTreatments, setSelectedTreatments] = useState(() =>
    initialTreatment ? [initialTreatment] : []
  );
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Scroll the form into view when arriving via an ?enquiry= deep-link.
  useEffect(() => {
    const search = routerLocation.search || window.location.search;
    if (!search || !new URLSearchParams(search).get("enquiry")) return;
    const el = document.querySelector('[data-testid="enquiry-form"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search]);

  // If admin renamed/repriced the offer after first render, sync the
  // treatment value once live settings arrive (only when treatment is still
  // empty or still pointing to a stale special). Also smoothly scrolls the
  // form into view when a special arrives via deep-link.
  useEffect(() => {
    if (!allSpecialItems.length) return;
    const search = routerLocation.search || window.location.search;
    if (!search) return;
    const wanted = new URLSearchParams(search).get("special");
    if (!wanted) return;
    const match = allSpecialItems.find(
      (it) => it.name.trim().toLowerCase() === wanted.trim().toLowerCase()
    );
    if (!match) return;
    const target = `✨ Special: ${match.name} (${match.price})`;
    setSelectedTreatments((list) => {
      if (list.includes(target)) return list;
      const staleIdx = list.findIndex((t) => t.startsWith("✨ Special:"));
      if (staleIdx >= 0) {
        const next = [...list];
        next[staleIdx] = target;
        return next;
      }
      return list.length === 0 ? [target] : list;
    });
    const el = document.querySelector('[data-testid="enquiry-form"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search, allSpecialItems.length]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || selectedTreatments.length === 0 || !form.preferred_time) {
      return toast.error("Please fill in all required fields.");
    }
    if (!acknowledged) {
      return toast.error("Please acknowledge the booking policy to continue.");
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enquiries`, {
        ...form,
        treatment: selectedTreatments.join(" + "),
      });
      setConfirmed(res.data);
      toast.success("Enquiry sent! Deborah will be in touch shortly.");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof raw === "string" && raw.trim()) {
        msg = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        // FastAPI + Pydantic v2 validation errors come as an array of objects.
        msg = raw
          .map((e) => (e && typeof e === "object" ? e.msg || "" : String(e)))
          .filter(Boolean)
          .join("; ") || msg;
      } else if (raw && typeof raw === "object" && typeof raw.msg === "string") {
        msg = raw.msg;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div
        className="bg-grain min-h-[60vh] flex items-center"
        data-testid="enquiry-confirmation"
      >
        <div className="mx-auto max-w-2xl px-6 lg:px-10 py-20 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-plum text-white">
            <Check size={28} />
          </div>
          <h1 className="font-display text-5xl text-plum-deep mt-6">Thank you</h1>
          <p className="mt-4 text-foreground/75 text-lg">
            Your enquiry has been received. Deborah will get back to you very soon
            to confirm a time that suits.
          </p>
          <div className="mt-10 bg-white rounded-3xl p-8 border border-blush-deep/40 text-left space-y-3">
            <Row label="Name" value={confirmed.name} />
            <Row label="Phone" value={confirmed.phone} />
            <Row label="Email" value={confirmed.email} />
            <Row label="Treatment(s)" value={confirmed.treatment} />
            <Row label="Preferred time" value={confirmed.preferred_time} />
            {confirmed.notes && <Row label="Notes" value={confirmed.notes} />}
          </div>
          <div className="mt-8 text-left">
            <PoliteNotice compact />
          </div>
          <p className="mt-8 text-sm text-foreground/60">
            Prefer to chat? Call Deborah on{" "}
            <a href={`tel:${BRAND.phoneTel}`} className="text-plum underline underline-offset-4">
              {BRAND.phone}
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="bg-grain">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
        <header className="text-center mb-10 sm:mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Contact & Booking Enquiry
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-plum-deep mt-4">
            Request your treatment
          </h1>
          <p className="mt-5 text-foreground/70 max-w-xl mx-auto leading-relaxed">
            Pop your details in below and Deborah will personally come back to
            you to confirm a date and time.
          </p>
          <div
            data-testid="gift-voucher-hint"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-champagne/60 bg-blush/60 px-4 sm:px-5 py-2 text-xs sm:text-sm text-plum"
          >
            <span aria-hidden>🎁</span>
            <span>
              <strong>Gift vouchers available</strong> · just ask in the enquiry
              section below.
            </span>
          </div>
        </header>

        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10">
          {/* SIDEBAR */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-plum text-white rounded-3xl p-8">
              <h3 className="font-display text-3xl">Get in touch</h3>
              <div className="mt-6 space-y-4 text-sm">
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-3 hover:text-champagne transition"
                >
                  <span className="rounded-full bg-white/10 p-2"><Phone size={14} /></span>
                  {BRAND.phone}
                </a>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 hover:text-champagne transition break-all"
                >
                  <span className="rounded-full bg-white/10 p-2"><Mail size={14} /></span>
                  {BRAND.email}
                </a>
                <a
                  href={BRAND.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  className="flex items-center gap-3 hover:text-champagne transition"
                >
                  <span className="rounded-full bg-white/10 p-2"><Facebook size={14} /></span>
                  Deborah Louise Beauty - Cornwall
                </a>
              </div>
            </div>

            {LOCATIONS.reduce((studios, l) => {
              const existing = studios.find((s) => s.address === l.address);
              if (existing) {
                existing.hours.push(l.hours);
                existing.name = existing.name.split(" · ")[0].replace(/\s*\((Mon|Tues|Wednes|Thurs|Fri|Satur|Sun)day\)\s*$/i, "");
              } else {
                studios.push({ id: l.id, name: l.name, address: l.address, hours: [l.hours] });
              }
              return studios;
            }, []).map((l) => (
              <div
                key={l.id}
                className="bg-white rounded-3xl p-6 border border-blush-deep/40"
                data-testid={`contact-location-${l.id}`}
              >
                <div className="flex items-start gap-3">
                  <MapPin className="text-plum mt-1 shrink-0" size={16} />
                  <div>
                    <div className="font-display text-xl text-plum">{l.name}</div>
                    <div className="text-sm text-foreground/65 mt-1">{l.address}</div>
                    {l.hours.map((h, i) => (
                      <div key={i} className="text-sm text-plum mt-2 font-semibold">{h}</div>
                    ))}
                    {/(molesworth|elixir)/i.test(`${l.name} ${l.address}`) && (
                      <div
                        data-testid={`walk-in-badge-${l.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-champagne/15 border border-champagne/50 text-plum px-3 py-1 text-xs font-semibold"
                      >
                        <DoorOpen size={12} /> Walk-in appointments available
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 rounded-2xl overflow-hidden border border-blush-deep/40">
                  <iframe
                    title={`Map – ${l.name}`}
                    data-testid={`location-map-${l.id}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(l.address)}&output=embed`}
                    className="w-full h-44 block"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(l.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`location-directions-${l.id}`}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-plum font-semibold hover:text-plum-light transition"
                >
                  <Navigation size={14} /> Get directions
                </a>
              </div>
            ))}
          </aside>

          {/* ENQUIRY FORM */}
          <section className="lg:col-span-8 space-y-6">
            <PoliteNotice />

            <form
              onSubmit={onSubmit}
              data-testid="enquiry-form"
              className="bg-white rounded-2xl sm:rounded-3xl border border-blush-deep/40 p-6 sm:p-8 md:p-10 space-y-5"
            >
              <h3 className="font-display text-2xl sm:text-3xl text-plum-deep">Your details</h3>
              <p className="text-foreground/65 text-sm">
                All fields below are required so Deborah can contact you back.
              </p>

              <div className="grid md:grid-cols-2 gap-4 pt-2">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Contact number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                    className="mt-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    className="mt-2"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="treatment">Treatments — add one or more</Label>
                  {allSpecialItems.length > 0 && (
                    <p
                      data-testid="treatment-offer-hint"
                      className="text-xs text-plum mt-1.5 mb-1 inline-flex items-center gap-1.5 rounded-full bg-blush/70 border border-champagne/60 px-3 py-1"
                    >
                      <span aria-hidden>✨</span>
                      Look out for{" "}
                      <strong>this month's specials & events</strong> at the top of the list.
                    </p>
                  )}
                  <Select
                    value=""
                    onValueChange={(v) =>
                      setSelectedTreatments((list) =>
                        list.includes(v) ? list : [...list, v]
                      )
                    }
                  >
                    <SelectTrigger
                      id="treatment"
                      data-testid="form-treatment"
                      className="mt-2"
                    >
                      <SelectValue
                        placeholder={
                          selectedTreatments.length
                            ? "Add another treatment?"
                            : "Which treatment are you interested in?"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {allSpecialItems.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-plum">
                            ✨ This month's specials & events
                          </SelectLabel>
                          {allSpecialItems.map((it) => (
                            <SelectItem
                              key={`offer-${it.name}`}
                              value={`✨ Special: ${it.name} (${it.price})`}
                              data-testid={`opt-offer-${it.name
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/^-|-$/g, "")}`}
                            >
                              ✨ {it.name} · {it.price}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      <SelectGroup>
                        <SelectLabel>Massage</SelectLabel>
                        {TREATMENTS.massage.map((t) => (
                          <SelectItem
                            key={t.name}
                            value={`${t.name} (${t.duration} · ${t.price})`}
                            data-testid={`opt-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {t.name} · {t.duration} · {t.price}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Beauty</SelectLabel>
                        {TREATMENTS.beauty.map((t) => (
                          <SelectItem
                            key={t.name}
                            value={`${t.name} (${t.duration} · ${t.price})`}
                            data-testid={`opt-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {t.name} · {t.duration} · {t.price}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Other</SelectLabel>
                        <SelectItem
                          value="Gift voucher enquiry"
                          data-testid="opt-gift-voucher"
                        >
                          🎁 Gift voucher enquiry
                        </SelectItem>
                        <SelectItem
                          value="Pamper party enquiry"
                          data-testid="opt-pamper-party"
                        >
                          🎉 Pamper party enquiry
                        </SelectItem>
                        <SelectItem
                          value="Mobile treatment enquiry"
                          data-testid="opt-mobile-treatment"
                        >
                          🏡 Mobile treatment enquiry
                        </SelectItem>
                        <SelectItem value="Not sure / would like advice" data-testid="opt-not-sure">
                          Not sure / would like advice
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {selectedTreatments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="selected-treatments">
                      {selectedTreatments.map((t, i) => (
                        <span
                          key={t}
                          data-testid={`selected-treatment-chip-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-plum text-white pl-4 pr-1.5 py-1.5 text-xs font-medium"
                        >
                          {t}
                          <button
                            type="button"
                            aria-label={`Remove ${t}`}
                            data-testid={`remove-treatment-${i}`}
                            onClick={() =>
                              setSelectedTreatments((list) => list.filter((x) => x !== t))
                            }
                            className="rounded-full hover:bg-white/20 p-1 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-foreground/50 mt-2">
                    Booking more than one treatment? Just pick again from the list to
                    add it.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="preferred_time">Day & time requested</Label>
                  <Textarea
                    id="preferred_time"
                    data-testid="form-preferred-time"
                    value={form.preferred_time}
                    onChange={update("preferred_time")}
                    required
                    rows={3}
                    className="mt-2"
                    placeholder="e.g. Friday afternoon, or any Saturday morning. Let Deborah know which day(s) and rough time(s) suit you."
                  />
                  <p className="text-xs text-foreground/55 mt-2">
                    Deborah is available Thursdays (Dunveth), Fridays & Saturdays (Elixir
                    Health).
                  </p>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="notes">Anything else? (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="form-notes"
                    value={form.notes}
                    onChange={update("notes")}
                    rows={3}
                    className="mt-2"
                    placeholder="Pregnancy, allergies, areas to focus on, first visit, etc."
                  />
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-champagne/60 bg-blush/60 p-4">
                <Checkbox
                  id="ack-policy"
                  data-testid="form-acknowledge"
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(Boolean(v))}
                  className="mt-0.5"
                />
                <label
                  htmlFor="ack-policy"
                  className="text-sm text-foreground/80 leading-snug cursor-pointer"
                >
                  I understand that a <strong className="text-plum">50% deposit</strong> will
                  be taken to secure my slot, that I must give at least{" "}
                  <strong className="text-plum">24 hours notice</strong> to amend or
                  cancel, and that late cancellations or no-shows will be charged in full.
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting || !acknowledged}
                data-testid="form-submit-btn"
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-plum text-white px-8 py-3.5 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send enquiry"} <ChevronRight size={16} />
              </button>
            </form>
          </section>
        </div>

        {/* FAQ */}
        {FAQS.length > 0 && (
        <section className="mt-16 sm:mt-20 max-w-3xl mx-auto" data-testid="faq-section">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
              Good to know
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-plum-deep mt-3">
              Frequently asked questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="bg-white rounded-3xl border border-blush-deep/40 px-6 sm:px-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-blush-deep/40">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-left font-medium text-plum-deep hover:text-plum hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${i}`}
                  className="text-foreground/70 leading-relaxed"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
        )}
      </div>
    </div>
  );
}
